import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Edit,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import Pagination from '../../../shared/components/Pagination';

import {
  storeService,
} from '../services/storeService';

import type {
  StoreListItem,
} from '../types/store.types';

interface StoreFormData {
  storeName: string;
  storePhone: string;
  storeAddress: string;
}

const ITEMS_PER_PAGE = 5;

const createInitialFormData =
  (): StoreFormData => ({
    storeName: '',
    storePhone: '',
    storeAddress: '',
  });

/**
 * 전화번호 자동 하이픈 포맷팅
 */
const formatPhoneNumber = (
  value: string,
): string => {
  const numbers =
    value.replace(
      /[^0-9]/g,
      '',
    );

  if (
    numbers.startsWith(
      '02',
    )
  ) {
    if (
      numbers.length <= 2
    ) {
      return numbers;
    }

    if (
      numbers.length <= 6
    ) {
      return `${numbers.slice(
        0,
        2,
      )}-${numbers.slice(2)}`;
    }

    return `${numbers.slice(
      0,
      2,
    )}-${numbers.slice(
      2,
      6,
    )}-${numbers.slice(
      6,
      10,
    )}`;
  }

  if (
    numbers.length <= 3
  ) {
    return numbers;
  }

  if (
    numbers.length <= 7
  ) {
    return `${numbers.slice(
      0,
      3,
    )}-${numbers.slice(3)}`;
  }

  return `${numbers.slice(
    0,
    3,
  )}-${numbers.slice(
    3,
    7,
  )}-${numbers.slice(
    7,
    11,
  )}`;
};

const SuperAdminStorePage:
  React.FC = () => {
  const modal =
    useModal();

  const [
    stores,
    setStores,
  ] =
    useState<
      StoreListItem[]
    >([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  /*
   * Modal
   */
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState(false);

  const [
    editingStore,
    setEditingStore,
  ] =
    useState<
      StoreListItem | null
    >(null);

  const [
    formData,
    setFormData,
  ] =
    useState<StoreFormData>(
      createInitialFormData,
    );

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  /**
   * 지점 목록 조회
   */
  const loadStores =
    async (): Promise<void> => {
      try {
        setLoading(true);

        const data =
          await storeService
            .getStores();

        setStores(data);
      } catch (
        error: unknown
      ) {
        console.error(
          '지점 목록 로딩 실패:',
          error,
        );

        await modal.alert(
          '지점 목록을 불러오지 못했습니다.',
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    void loadStores();
  }, []);

  /**
   * 추가 Modal 열기
   */
  const handleOpenAddModal =
    (): void => {
      setEditingStore(
        null,
      );

      setFormData(
        createInitialFormData(),
      );

      setIsModalOpen(
        true,
      );
    };

  /**
   * 수정 Modal 열기
   */
  const handleOpenEditModal = (
    store: StoreListItem,
  ): void => {
    setEditingStore(
      store,
    );

    setFormData({
      storeName:
        store.storeName,

      storePhone:
        store.storePhone,

      storeAddress:
        store.storeAddress,
    });

    setIsModalOpen(
      true,
    );
  };

  /**
   * Modal 닫기
   */
  const handleCloseModal =
    (): void => {
      if (submitting) {
        return;
      }

      setIsModalOpen(
        false,
      );

      setEditingStore(
        null,
      );

      setFormData(
        createInitialFormData(),
      );
    };

  /**
   * 저장
   *
   * editingStore 존재
   * → 수정
   *
   * 없음
   * → 신규 생성
   */
  const handleSubmit =
    async (): Promise<void> => {
      const storeName =
        formData.storeName.trim();

      const storePhone =
        formData.storePhone.trim();

      const storeAddress =
        formData.storeAddress.trim();

      if (
        !storeName ||
        !storePhone ||
        !storeAddress
      ) {
        await modal.alert(
          '모든 필드를 입력해주세요.',
        );

        return;
      }

      try {
        setSubmitting(true);

        if (
          editingStore
        ) {
          await storeService
            .updateStore(
              editingStore.storeId,
              {
                storeName,
                storePhone,
                storeAddress,
              },
            );

          await modal.alert(
            '지점 정보가 수정되었습니다.',
          );
        } else {
          await storeService
            .createStore({
              storeName,
              storePhone,

              /*
               * 현재 백엔드 생성 DTO가
               * storeStreet를 사용하므로
               * address → street로 변환
               */
              storeStreet:
                storeAddress,
            });

          await modal.alert(
            '지점이 추가되었습니다.',
          );
        }

        await loadStores();

        setIsModalOpen(
          false,
        );

        setEditingStore(
          null,
        );

        setFormData(
          createInitialFormData(),
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '지점 저장 실패:',
          error,
        );

        await modal.alert(
          editingStore
            ? '지점 수정에 실패했습니다.'
            : '지점 추가에 실패했습니다.',
        );
      } finally {
        setSubmitting(
          false,
        );
      }
    };

  /**
   * 지점 삭제
   */
  const handleDelete =
    async (
      store: StoreListItem,
    ): Promise<void> => {
      const confirmed =
        await modal.confirm(
          `"${store.storeName}" 지점을 삭제하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        await storeService
          .deleteStore(
            store.storeId,
          );

        await modal.alert(
          '지점이 삭제되었습니다.',
        );

        await loadStores();
      } catch (
        error: unknown
      ) {
        console.error(
          '지점 삭제 실패:',
          error,
        );

        await modal.alert(
          '삭제에 실패했습니다.',
        );
      }
    };

  /**
   * 검색 필터
   */
  const filteredStores =
    useMemo(() => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      if (!search) {
        return stores;
      }

      return stores.filter(
        (store) =>
          store.storeName
            .toLowerCase()
            .includes(
              search,
            ) ||
          store.storeAddress
            .toLowerCase()
            .includes(
              search,
            ) ||
          store.storePhone
            .toLowerCase()
            .includes(
              search,
            ),
      );
    }, [
      stores,
      searchTerm,
    ]);

  /**
   * 페이지네이션
   */
  const paginatedStores =
    useMemo(() => {
      const startIndex =
        (currentPage - 1) *
        ITEMS_PER_PAGE;

      const endIndex =
        startIndex +
        ITEMS_PER_PAGE;

      return filteredStores.slice(
        startIndex,
        endIndex,
      );
    }, [
      filteredStores,
      currentPage,
    ]);

  /**
   * 검색어 변경 시
   * 첫 페이지로 이동
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  /**
   * 삭제 등으로 현재 페이지가
   * 최대 페이지를 넘어가는 경우 보정
   */
  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredStores.length /
            ITEMS_PER_PAGE,
        ),
      );

    if (
      currentPage >
      totalPages
    ) {
      setCurrentPage(
        totalPages,
      );
    }
  }, [
    currentPage,
    filteredStores.length,
  ]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-20-regular text-gray-600">
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-black sm:text-3xl">
            지점 관리
          </h1>

          <p className="mt-1 text-sm text-gray-600 sm:text-base">
            홀덤펍 지점 정보를
            관리합니다.
          </p>
        </div>

        <button
          type="button"
          onClick={
            handleOpenAddModal
          }
          className="flex items-center justify-center gap-2 rounded-lg bg-main px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-main-medium sm:px-6 sm:text-base"
        >
          <span className="text-lg leading-none">
            +
          </span>

          <span>
            지점 추가
          </span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />

          <input
            type="text"
            placeholder="지점명, 주소, 전화번호로 검색..."
            value={
              searchTerm
            }
            onChange={(
              event,
            ) =>
              setSearchTerm(
                event.target
                  .value,
              )
            }
            className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm outline-none transition focus:border-main sm:text-base"
          />
        </div>
      </div>

      {/* Store List */}
      <div className="space-y-4">
        {paginatedStores.length ===
        0 ? (
          <div className="py-12 text-center text-sm text-gray-500 sm:text-base">
            {searchTerm
              ? '검색 결과가 없습니다.'
              : '등록된 지점이 없습니다.'}
          </div>
        ) : (
          paginatedStores.map(
            (store) => (
              <div
                key={
                  store.storeId
                }
                className="rounded-lg border border-gray-300 bg-white p-4 transition-shadow hover:shadow-md sm:p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="mb-2 truncate text-lg font-semibold text-black sm:text-xl">
                      {
                        store.storeName
                      }
                    </h3>

                    <p className="mb-1 break-words text-sm text-gray-600 sm:text-base">
                      {
                        store.storeAddress
                      }
                    </p>

                    <p className="flex items-center gap-2 text-sm text-gray-600 sm:text-base">
                      <span
                        aria-hidden="true"
                      >
                        📞
                      </span>

                      <span>
                        {
                          store.storePhone
                        }
                      </span>
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        handleOpenEditModal(
                          store,
                        )
                      }
                      className="rounded-lg p-2 transition-colors hover:bg-gray-100"
                      title="수정"
                      aria-label={`${store.storeName} 지점 수정`}
                    >
                      <Edit
                        size={20}
                        className="text-gray-600"
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        void handleDelete(
                          store,
                        )
                      }
                      className="rounded-lg p-2 transition-colors hover:bg-red-50"
                      title="삭제"
                      aria-label={`${store.storeName} 지점 삭제`}
                    >
                      <Trash2
                        size={20}
                        className="text-red-500"
                      />
                    </button>
                  </div>
                </div>
              </div>
            ),
          )
        )}
      </div>

      {/* Pagination */}
      {filteredStores.length >
        0 && (
        <Pagination
          currentPage={
            currentPage
          }
          totalItems={
            filteredStores.length
          }
          itemsPerPage={
            ITEMS_PER_PAGE
          }
          onPageChange={
            setCurrentPage
          }
        />
      )}

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onMouseDown={(
            event,
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              handleCloseModal();
            }
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl sm:p-6">
            {/* Modal Header */}
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-black sm:text-xl">
                {editingStore
                  ? '지점 수정'
                  : '지점 추가'}
              </h2>

              <button
                type="button"
                onClick={
                  handleCloseModal
                }
                disabled={
                  submitting
                }
                className="rounded p-1 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="모달 닫기"
              >
                <X
                  size={20}
                  className="text-gray-500"
                />
              </button>
            </div>

            <div className="space-y-4">
              {/* Store Name */}
              <div>
                <label
                  htmlFor="store-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  지점명
                </label>

                <input
                  id="store-name"
                  type="text"
                  value={
                    formData.storeName
                  }
                  onChange={(
                    event,
                  ) =>
                    setFormData(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        storeName:
                          event
                            .target
                            .value,
                      }),
                    )
                  }
                  disabled={
                    submitting
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-main disabled:bg-gray-100 sm:text-base"
                  placeholder="지점명을 입력하세요"
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="store-phone"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  전화번호
                </label>

                <input
                  id="store-phone"
                  type="tel"
                  value={
                    formData.storePhone
                  }
                  onChange={(
                    event,
                  ) =>
                    setFormData(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        storePhone:
                          formatPhoneNumber(
                            event
                              .target
                              .value,
                          ),
                      }),
                    )
                  }
                  disabled={
                    submitting
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-main disabled:bg-gray-100 sm:text-base"
                  placeholder="02-1234-5678"
                />
              </div>

              {/* Address */}
              <div>
                <label
                  htmlFor="store-address"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  주소
                </label>

                <input
                  id="store-address"
                  type="text"
                  value={
                    formData.storeAddress
                  }
                  onChange={(
                    event,
                  ) =>
                    setFormData(
                      (
                        previous,
                      ) => ({
                        ...previous,

                        storeAddress:
                          event
                            .target
                            .value,
                      }),
                    )
                  }
                  disabled={
                    submitting
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-main disabled:bg-gray-100 sm:text-base"
                  placeholder="주소를 입력하세요"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={
                  handleCloseModal
                }
                disabled={
                  submitting
                }
                className="flex-1 rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
              >
                취소
              </button>

              <button
                type="button"
                onClick={() =>
                  void handleSubmit()
                }
                disabled={
                  submitting
                }
                className="flex-1 rounded-lg bg-main py-3 text-sm font-medium text-white transition-colors hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
              >
                {submitting
                  ? '저장 중...'
                  : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminStorePage;